import React, { useState } from "react"
import { Head } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Mail, Lock, Eye, EyeOff } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { useForm } from '@inertiajs/react'
import { useUsers } from "@/lib/contexts/user-context"
import { router } from "@inertiajs/react"

export default function LoginPage() {
  const { toast } = useToast()
  const { setCurrentUser, currentUser } = useUsers()
  const [showPassword, setShowPassword] = useState(false)
  const [isRedirecting, setIsRedirecting] = useState(false)
    
  const { data, setData, post, processing, errors } = useForm({
    email: '',
    password: '',
    remember: false
  })

  // Helper to render email error with clickable link if provided by backend
  const renderEmailError = () => {
    if (!errors.email) return null;
    const emailError = errors.email;
    const anchorRegex = /<a[^>]*href=["']?([^"'>]+)[^>]*>(.*?)<\/a>/i;
    const match = emailError.match(anchorRegex);

    if (match) {
      const href = match[1];
      const linkText = match[2];
      const parts = emailError.split(match[0]);
      const beforeLink = parts[0];
      const afterLink = parts[1] ?? "";

      return (
        <>
          {beforeLink}
          <a
            href={href}
            className="font-medium text-blue-600 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {linkText}
          </a>
          {afterLink}
        </>
      );
    }

    return emailError;
  };

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (isRedirecting) return; // Prevent multiple submissions
        
    setIsRedirecting(true);
    
    post('/login', {
      preserveScroll: true,
      onSuccess: async (page) => {
        try {
          
          // Check if user data is available from the backend response
          if (page.props.user) {
            // Set the current user from the backend data
            const userData = {
              ...page.props.user,
              roles: page.props.user.roles || [],
              permissions: page.props.user.permissions || []
            }
            
            
            // Set the user in context and wait for it to complete
            await setCurrentUser(userData);
            
            // Show success toast
            toast({
              title: "Login successful",
              description: "Welcome back!",
            })
            
            router.visit(page.props.redirect || '/', {
              preserveScroll: true,
              onFinish: () => setIsRedirecting(false)
            });
          } else {
            setIsRedirecting(false);
            toast({
              title: "Login error",
              description: "User data not received from server",
              variant: "destructive",
            });
          }
        } catch (error) {
          console.error('Error during login success handling:', error);
          setIsRedirecting(false);
          toast({
            title: "Login error",
            description: "An error occurred while processing login",
            variant: "destructive",
          });
        }
      },
      onError: (errors) => {
        setIsRedirecting(false);

        // Prepare description content. If the server provided an HTML anchor tag, render it as real HTML
        let descriptionContent;
        if (errors.email) {
          const emailError = errors.email;
          const anchorRegex = /<a[^>]*href=["']?([^"'>]+)[^>]*>(.*?)<\/a>/i;
          const match = emailError.match(anchorRegex);

          if (match) {
            const href = match[1];
            const linkText = match[2];
            const parts = emailError.split(match[0]);
            const beforeLink = parts[0];
            const afterLink = parts[1] ?? "";

            descriptionContent = (
              <span>
                {beforeLink}
                <a
                  href={href}
                  className="font-medium text-blue-600 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {linkText}
                </a>
                {afterLink}
              </span>
            );
          } else {
            descriptionContent = emailError;
          }
        } else {
          descriptionContent = "Invalid email or password";
        }

        toast({
          title: "Login failed",
          description: descriptionContent,
          variant: "destructive",
        });
      }
    })
  }

  return (
    <>
      <Head title="DME | Login" />
      <div className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-background via-background to-muted/30 z-0">
        {/* Dot pattern grid */}
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="100" height="100" patternUnits="userSpaceOnUse">
                <circle cx="50" cy="50" r="1" fill="currentColor" className="text-muted-foreground" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Workflow nodes - HIDDEN ON SMALLER SCREENS */}
        <div className="hidden lg:block absolute top-[10%] left-[15%] w-16 h-16 rounded-lg border-2 border-amber-500/30 bg-amber-500/10 shadow-lg"></div>
        <div className="hidden lg:block absolute top-[25%] left-[25%] w-20 h-20 rounded-lg border-2 border-primary/30 bg-primary/10 shadow-lg"></div>
        <div className="hidden lg:block absolute top-[60%] left-[10%] w-24 h-16 rounded-lg border-2 border-blue-500/30 bg-blue-500/10 shadow-lg"></div>
        <div className="hidden lg:block absolute top-[15%] right-[20%] w-20 h-20 rounded-lg border-2 border-green-500/30 bg-green-500/10 shadow-lg"></div>
        <div className="hidden lg:block absolute top-[40%] right-[15%] w-16 h-16 rounded-lg border-2 border-purple-500/30 bg-purple-500/10 shadow-lg"></div>
        <div className="hidden lg:block absolute top-[70%] right-[25%] w-24 h-16 rounded-lg border-2 border-red-500/30 bg-red-500/10 shadow-lg"></div>

        {/* Workflow icons - REDUCED AND HIDDEN ON SMALLER SCREENS */}
        <div className="hidden md:block absolute top-[5%] left-[40%] text-amber-500/40 animate-float-slow">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="30"
            height="30"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
          </svg>
        </div>

        {/* User Management Icons - REDUCED AND HIDDEN ON SMALLER SCREENS */}
        <div className="hidden md:block absolute top-[15%] left-[60%] text-primary/40 animate-float">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
        </div>

        {/* MOST BACKGROUND ELEMENTS HIDDEN ON SMALLER SCREENS */}
        <div className="hidden lg:block absolute top-[75%] left-[40%] text-blue-500/40 animate-float-slow">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg>
        </div>

        {/* Task Management Icons - HIDDEN ON SMALLER SCREENS */}
        <div className="hidden lg:block absolute top-[30%] left-[5%] text-green-500/40 animate-float">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
        </div>

        {/* REMAINING BACKGROUND ELEMENTS HIDDEN ON SMALLER SCREENS */}
        <div className="hidden lg:block absolute top-[50%] left-[80%] text-purple-500/40 animate-float-slow">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 2v4"></path>
            <path d="M12 18v4"></path>
            <path d="M4.93 4.93l2.83 2.83"></path>
            <path d="M16.24 16.24l2.83 2.83"></path>
            <path d="M2 12h4"></path>
            <path d="M18 12h4"></path>
            <path d="M4.93 19.07l2.83-2.83"></path>
            <path d="M16.24 7.76l2.83-2.83"></path>
          </svg>
        </div>

        {/* ONLY SHOWING ESSENTIAL BACKGROUND ELEMENTS */}
      </div>

      {/* Login form */}
      <div className="w-full max-w-[400px] px-4">
        <div className="bg-card/80 backdrop-blur-sm rounded-lg border shadow-lg p-4">
          <div className="flex flex-col items-center text-center mb-4">
            <h1 className="text-xl font-semibold tracking-tight">Welcome to your DME</h1>
            <p className="text-xs text-muted-foreground">Workflow, Task & User Management System</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="email" className="text-sm">
                Email
              </Label>
                <div className="relative">
                <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                  placeholder="name@example.com"
                    type="email"
                    autoCapitalize="none"
                    autoComplete="email"
                    autoCorrect="off"
                  className="pl-10 h-9"
                    value={data.email}
                    onChange={e => setData('email', e.target.value)}
                  />
                {errors.email && (
                  <p className="text-sm text-destructive mt-1">
                    {renderEmailError()}
                  </p>
                )}
                </div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm">
                  Password
                </Label>
                <a href="/forgot-password" className="text-xs text-primary hover:underline">
                  Forgot?
                </a>
              </div>
                <div className="relative">
                <Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                  className="pl-10 pr-10 h-9"
                    value={data.password}
                    onChange={e => setData('password', e.target.value)}
                  />
                  <button
                    type="button"
                  className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                {errors.password && (
                  <p className="text-sm text-destructive mt-1">{errors.password}</p>
                )}
              </div>
            </div>

            <Button type="submit" className="w-full h-9 dark:text-white" disabled={processing}>
              {processing ? "Signing in..." : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
    </>
  )
}
